import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import { CloudUpload, ExpandMore, ExpandLess } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../api";
import WizardPageShell from "./WizardPageShell";

export default function WizardAiTutoring() {
  const inputRef = useRef(null);
  const [phase, setPhase] = useState("capture"); // capture | scanning | results
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [hints, setHints] = useState([]);
  const [answerOpen, setAnswerOpen] = useState(false);
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [answerContent, setAnswerContent] = useState("");
  const [explanationContent, setExplanationContent] = useState("");
  const [answerLoading, setAnswerLoading] = useState(false);
  const [explanationLoading, setExplanationLoading] = useState(false);

  const reset = () => {
    setPhase("capture");
    setPreviewUrl(null);
    setError("");
    setSessionId("");
    setQuestionText("");
    setHints([]);
    setAnswerOpen(false);
    setExplanationOpen(false);
    setAnswerContent("");
    setExplanationContent("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const scanFile = async (file) => {
    if (!file) return;
    setError("");
    setPhase("scanning");
    setPreviewUrl(URL.createObjectURL(file));
    const form = new FormData();
    form.append("image", file);
    try {
      const res = await api.post("student/wizard/ai-tutoring/scan", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res.data?.data || {};
      setSessionId(String(data.session_id || ""));
      setQuestionText(String(data.question_text || ""));
      setHints(Array.isArray(data.hints) ? data.hints.map(String) : []);
      setPhase("results");
    } catch (e) {
      const msg = e?.response?.data?.message || "Could not scan the question.";
      setError(msg);
      setPhase("capture");
      toast.error(msg);
    }
  };

  const loadSection = async (section) => {
    if (!sessionId) return;
    const isAnswer = section === "answer";
    if (isAnswer) {
      if (answerContent) {
        setAnswerOpen((v) => !v);
        return;
      }
      setAnswerLoading(true);
      setAnswerOpen(true);
    } else {
      if (explanationContent) {
        setExplanationOpen((v) => !v);
        return;
      }
      setExplanationLoading(true);
      setExplanationOpen(true);
    }
    try {
      const res = await api.post("student/wizard/ai-tutoring/section", {
        session_id: sessionId,
        section,
      });
      const content = String(res.data?.data?.content || "");
      if (isAnswer) setAnswerContent(content);
      else setExplanationContent(content);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not load this section.");
    } finally {
      if (isAnswer) setAnswerLoading(false);
      else setExplanationLoading(false);
    }
  };

  return (
    <WizardPageShell
      title="AI Tutoring"
      subtitle="Upload a tricky question for step-by-step help"
    >
      {phase === "capture" || phase === "scanning" ? (
        <Stack spacing={2} alignItems="flex-start">
          <Typography color="text.secondary">
            Upload a clear photo of the question. We will extract the text and give you hints to solve it.
          </Typography>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => scanFile(e.target.files?.[0])}
          />
          <Button
            variant="contained"
            startIcon={phase === "scanning" ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}
            disabled={phase === "scanning"}
            onClick={() => inputRef.current?.click()}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {phase === "scanning" ? "Scanning…" : "Upload question image"}
          </Button>
          {previewUrl && phase === "scanning" ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Question preview"
              sx={{ maxWidth: "100%", maxHeight: 280, borderRadius: 2, border: "1px solid #E2E8F0" }}
            />
          ) : null}
        </Stack>
      ) : (
        <Stack spacing={2}>
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Scanned question"
              sx={{ maxWidth: "100%", maxHeight: 220, borderRadius: 2, border: "1px solid #E2E8F0", alignSelf: "flex-start" }}
            />
          ) : null}
          {questionText ? (
            <Box sx={{ p: 2, bgcolor: "#F8FAFC", borderRadius: 2, border: "1px solid #E2E8F0" }}>
              <Typography variant="overline" color="text.secondary">Question</Typography>
              <Typography sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>{questionText}</Typography>
            </Box>
          ) : null}
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Hints to solve
            </Typography>
            <Stack spacing={1}>
              {hints.map((hint, i) => (
                <Box
                  key={i}
                  sx={{ p: 1.5, borderRadius: 2, bgcolor: "#EFF6FF", border: "1px solid #BFDBFE" }}
                >
                  <Typography variant="body2">
                    <strong>{i + 1}.</strong> {hint}
                  </Typography>
                </Box>
              ))}
              {hints.length === 0 ? (
                <Typography color="text.secondary">No hints returned for this scan.</Typography>
              ) : null}
            </Stack>
          </Box>

          <SectionToggle
            label="Answer"
            tint="#059669"
            open={answerOpen}
            loading={answerLoading}
            content={answerContent}
            onClick={() => loadSection("answer")}
          />
          <SectionToggle
            label="Explanation"
            tint="#7C3AED"
            open={explanationOpen}
            loading={explanationLoading}
            content={explanationContent}
            onClick={() => loadSection("explanation")}
          />

          <Button variant="outlined" onClick={reset} sx={{ textTransform: "none", alignSelf: "flex-start" }}>
            Scan another question
          </Button>
        </Stack>
      )}
    </WizardPageShell>
  );
}

function SectionToggle({ label, tint, open, loading, content, onClick }) {
  return (
    <Box sx={{ border: `1px solid ${tint}44`, borderRadius: 2, overflow: "hidden" }}>
      <Button
        fullWidth
        onClick={onClick}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          fontWeight: 700,
          color: tint,
          bgcolor: `${tint}12`,
          borderRadius: 0,
          px: 2,
          py: 1.25,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: tint }} />
          {label}
        </Box>
      </Button>
      <Collapse in={open}>
        <Box sx={{ p: 2, bgcolor: "#fff" }}>
          {loading ? (
            <CircularProgress size={22} />
          ) : (
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{content || "No content."}</Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
